import React, { useEffect, useRef, useState } from "react";

import classNames from "classnames";

import Form from "../Form";
import FormErrors from "../FormErrors";
import TimezoneSelect from "../TimezoneSelect";
import GoogleSignIn from "../GoogleSignIn";

import styles from "./style.module.scss";
import { useRoutes, useUnauthenticatedRoutes } from "utils/routes";

type Props = {
  invite: {
    email: string;
    organizationName: string;
    invitedByFullname: string;
  };
  errors: string[];
  enterpriseEdition: boolean;
  googleAuthorizePath: string;
  googleClientId: string | null | undefined;
  marketplaceProvider?: string;
};

type SignupMethod = "email" | "google";

const Signup: React.FunctionComponent<Props> = ({
  invite,
  errors: initialErrors,
  enterpriseEdition,
  googleClientId,
  googleAuthorizePath,
  marketplaceProvider,
}) => {
  const [method, setMethod] = useState<SignupMethod>("email");
  const [errors, setErrors] = useState(initialErrors);
  const { marketplaceLink } = useRoutes();

  useEffect(() => {
    if (window.gtag) {
      window.gtag("event", "viewed", {
        event_category: "Signup Form",
        non_interaction: true,
      });
    }
  }, []);

  const trialAvailable = !invite && !enterpriseEdition && !marketplaceProvider;

  return (
    <>
      <HeaderNotice />
      <div className="flex-container">
        <div className="form-container">
          <div>
            {trialAvailable ? (
              <h1>Sign up for a free 14-day trial</h1>
            ) : (
              <h1>Sign up</h1>
            )}
            {invite && (
              <p>
                You're invited to the organization "{invite.organizationName}"
                by {invite.invitedByFullname}.
                <br />
                <br />
                Please sign up to accept the invitation.
              </p>
            )}
            {marketplaceProvider && marketplaceProvider === "aws" && (
              <p>
                Thank you for purchasing pganalyze through the AWS Marketplace.
                Please sign up to start using pganalyze.
                <br />
                <br />
                Already have a pganalyze account?{" "}
                <a href={marketplaceLink(marketplaceProvider)}>
                  Sign in to link your AWS account
                </a>
                .
              </p>
            )}
            {trialAvailable && (
              <p>
                Your trial account comes with the complete
                <br />
                pganalyze feature set. No credit-card required.
              </p>
            )}
          </div>
          {googleClientId && (
            <div className={styles.segmentedControl}>
              <button
                type="button"
                className={classNames(
                  "flex-1 py-2 px-4 text-sm font-medium text-center border-none cursor-pointer transition-colors duration-150 bg-white",
                  method === "email"
                    ? "text-[#333] shadow-[inset_0_-2px_0_#2ea9a9]"
                    : "text-[#595959] hover:text-[#333]",
                )}
                onClick={() => setMethod("email")}
              >
                Email
              </button>
              <button
                type="button"
                className={classNames(
                  "flex-1 py-2 px-4 text-sm font-medium text-center border-none cursor-pointer transition-colors duration-150 bg-white",
                  method === "google"
                    ? "text-[#333] shadow-[inset_0_-2px_0_#2ea9a9]"
                    : "text-[#595959] hover:text-[#333]",
                )}
                onClick={() => setMethod("google")}
              >
                Google
              </button>
            </div>
          )}
          {method === "email" && (
            <SignupPassword
              invite={invite}
              enterpriseEdition={enterpriseEdition}
              errors={errors}
              marketplaceProvider={marketplaceProvider}
            />
          )}
          {method === "google" && googleClientId && (
            <SignupGoogle
              invite={invite}
              enterpriseEdition={enterpriseEdition}
              errors={errors}
              setErrors={setErrors}
              googleAuthorizePath={googleAuthorizePath}
              googleClientId={googleClientId}
            />
          )}
        </div>
      </div>
    </>
  );
};

const SignupGoogle: React.FunctionComponent<
  Props & { setErrors: (value: string[]) => void }
> = ({
  errors,
  setErrors,
  invite,
  enterpriseEdition,
  googleAuthorizePath,
  googleClientId,
}) => {
  const formRef = useRef<HTMLFormElement>();
  return (
    <form ref={formRef} id="new_user">
      <FormErrors errors={errors} modelName="user" />
      {!enterpriseEdition && <OrganizationName invite={invite} />}
      <Email invite={invite} />
      <Timezone />
      <div>
        <GoogleSignIn
          authorizePath={googleAuthorizePath}
          googleClientId={googleClientId || ""}
          buttonText={
            (invite ? "Accept Invitation and " : "") + "Sign Up with Google"
          }
          errorHandler={(errors: string[]) => {
            setErrors(errors);
          }}
          formValidateHandler={(): boolean => {
            if (formRef.current?.reportValidity) {
              return formRef.current.reportValidity();
            } else if (formRef.current) {
              return formRef.current.checkValidity();
            } else {
              return true;
            }
          }}
          formDataHandler={(): FormData =>
            (formRef.current && new FormData(formRef.current)) || new FormData()
          }
          formSuccessHandler={(location: string): void => {
            if (window.gtag) {
              window.gtag("event", "submitted", {
                event_category: "Signup Form",
                event_label: "Google Auth",
              });
              window.gtag("event", "conversion", {
                send_to: "AW-792224255/eZy8CK2XnPgBEP_D4fkC",
                event_callback: () => {
                  document.location.href = location;
                },
              });
            } else {
              document.location.href = location;
            }
          }}
        />
      </div>
      <TermsNotice enterpriseEdition={enterpriseEdition} />
    </form>
  );
};

const HeaderNotice: React.FunctionComponent = () => {
  const { userSignIn } = useUnauthenticatedRoutes();
  return (
    <div className="header-notice">
      Already signed up? <a href={userSignIn()}>Log in now.</a>
    </div>
  );
};

const Timezone: React.FunctionComponent = () => {
  return <TimezoneSelect id="user_timezone" name="user[timezone]" />;
};

const TermsNotice: React.FunctionComponent<
  Pick<Props, "enterpriseEdition">
> = ({ enterpriseEdition }) => {
  if (enterpriseEdition) {
    return null;
  }

  return (
    <p className={styles.termsNotice}>
      By signing up you agree to the{" "}
      <a href="https://pganalyze.com/terms">Terms of Service</a> and{" "}
      <a href="https://pganalyze.com/privacy">Privacy Policy</a>.
    </p>
  );
};

const Email: React.FunctionComponent<Pick<Props, "invite">> = ({ invite }) => {
  return (
    <input
      className="form-control"
      required
      id="user_email"
      type="email"
      name="user[email]"
      defaultValue={invite && invite.email}
      readOnly={invite && !!invite.email}
      placeholder="Email"
    />
  );
};

const OrganizationName: React.FunctionComponent<Pick<Props, "invite">> = ({
  invite,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current && !invite) {
      inputRef.current.focus();
    }
  }, [invite]);

  if (invite) {
    return null;
  }
  return (
    <input
      ref={inputRef}
      className="form-control"
      required
      id="user_organization_name"
      type="text"
      name="user[organization_name]"
      placeholder="Organization / Company"
    />
  );
};

const SignupPassword: React.FunctionComponent<
  Pick<Props, "enterpriseEdition" | "invite" | "marketplaceProvider"> & {
    errors: string[];
  }
> = ({ errors, enterpriseEdition, invite, marketplaceProvider }) => {
  const { users } = useUnauthenticatedRoutes();
  return (
    <Form
      id="new_user"
      action={users()}
      method="post"
      eventCategory="Signup Form"
      eventLabel="Password Auth"
      eventConversion="AW-792224255/eZy8CK2XnPgBEP_D4fkC"
      insideSection
    >
      <FormErrors errors={errors} modelName="user" />
      {!enterpriseEdition && <OrganizationName invite={invite} />}
      <input
        className="form-control"
        required
        id="user_fullname"
        type="text"
        name="user[fullname]"
        placeholder="Full Name"
      />
      <Email invite={invite} />
      <input
        className="form-control"
        required
        id="user_password"
        type="password"
        name="user[password]"
        placeholder="Password"
      />
      <input
        className="form-control"
        required
        id="user_password_confirmation"
        type="password"
        name="user[password_confirmation]"
        placeholder="Password confirmation"
      />
      <Timezone />
      <input
        type="submit"
        name="commit"
        value={
          marketplaceProvider
            ? "Create Account"
            : invite
            ? "Accept Invitation and Create Account"
            : `Create Account and Start Your Free Trial`
        }
        className="btn btn-success"
      />

      <TermsNotice enterpriseEdition={enterpriseEdition} />
    </Form>
  );
};

export default Signup;
